import { Icon } from '@iconify/react'
import './IntelligentCheck.scss'

// Automated document pre-check (backend: app/document_review). Shadow mode —
// it informs the reviewer and never approves or rejects anything. Shared by
// the KYC queue/detail, the shipper ID detail and booking sender IDs.

export type IntelligentCheckVerdict = 'pass' | 'flag' | 'fail'

export interface IntelligentCheckResult {
  image: string
  code: string
  result: 'pass' | 'warn' | 'fail'
  detail: string | null
}

export interface IntelligentCheckReadout {
  documentType?: string
  legible?: boolean
  qualityIssues?: string[]
  fullName?: string | null
  idNumberLast4?: string | null
  documentDate?: string | null
  expiryDate?: string | null
  faceVisible?: boolean
  holdingIdDocument?: boolean
}

export interface IntelligentCheck {
  state: 'pending' | 'processing' | 'done' | 'failed'
  attempts?: number
  verdict?: IntelligentCheckVerdict | null
  checks?: IntelligentCheckResult[]
  extracted?: { [image: string]: IntelligentCheckReadout } | null
  model?: string | null
  completedAt?: string | null
  lastError?: string | null
  // Booking sender IDs only: the image URL this result is about.
  source?: string | null
}

const VERDICT_META: { [key: string]: { label: string; className: string; icon: string } } = {
  pass: { label: 'Pass', className: 'ic-pass', icon: 'mdi:check-circle' },
  flag: { label: 'Flag', className: 'ic-flag', icon: 'mdi:alert' },
  fail: { label: 'Fail', className: 'ic-fail', icon: 'mdi:close-circle' },
}

const CHECK_LABELS: { [key: string]: string } = {
  IMAGE_QUALITY: 'Image quality',
  TAMPER_SIGNS: 'Signs of editing',
  DOCUMENT_TYPE: 'Document type',
  ID_NUMBER: 'ID number',
  NAME: 'Name',
  DOCUMENT_DATE: 'Document date',
  ID_EXPIRED: 'Expiry',
  SELFIE_FACE: 'Face visible',
  SELFIE_HOLDING_ID: 'Holding ID',
}

const READ_TYPE_LABELS: { [key: string]: string } = {
  nin_slip: 'NIN slip',
  nin_card: 'NIN card',
  drivers_licence: "Driver's licence",
  passport: 'Passport',
  voters_card: "Voter's card",
  utility_bill: 'Utility bill',
  bank_statement: 'Bank statement',
  tenancy_agreement: 'Tenancy agreement',
  selfie_with_id: 'Selfie with ID',
  other_document: 'Other document',
  not_a_document: 'Not a document',
}

const humanise = (value: string) => value.replace(/_/g, ' ')

const RESULT_ICON = {
  pass: 'mdi:check-circle',
  warn: 'mdi:alert',
  fail: 'mdi:close-circle',
}

const IMAGE_RESULT_META = {
  pass: { label: 'No issues', className: 'ic-pass' },
  warn: { label: 'Needs a look', className: 'ic-flag' },
  fail: { label: 'Problem found', className: 'ic-fail' },
}

const worstResult = (checks: IntelligentCheckResult[]): IntelligentCheckResult['result'] =>
  checks.some((c) => c.result === 'fail')
    ? 'fail'
    : checks.some((c) => c.result === 'warn')
      ? 'warn'
      : 'pass'

/**
 * Compact verdict for tables and headers. Renders nothing when never queued,
 * unless `showUnchecked`. `compact` drops the "Intelligent check:" prefix for
 * a column that already says so.
 */
export const IntelligentCheckBadge = ({
  review,
  compact = false,
  showUnchecked = false,
}: {
  review?: IntelligentCheck | null
  compact?: boolean
  showUnchecked?: boolean
}) => {
  if (!review) {
    return showUnchecked ? (
      <span className='ic-badge ic-none' title='Uploaded before the check was switched on'>
        <Icon icon='mdi:minus-circle-outline' /> Not checked
      </span>
    ) : null
  }
  if (review.state === 'pending' || review.state === 'processing') {
    return (
      <span className='ic-badge ic-checking' title='Intelligent check in progress'>
        <Icon icon='mdi:progress-clock' /> Checking…
      </span>
    )
  }
  if (review.state === 'failed') {
    return (
      <span className='ic-badge ic-error' title={review.lastError || ''}>
        <Icon icon='mdi:robot-off-outline' /> Check failed
      </span>
    )
  }
  const meta = review.verdict ? VERDICT_META[review.verdict] : null
  if (!meta) return null
  return (
    <span className={`ic-badge ${meta.className}`} title='Intelligent check (advisory)'>
      <Icon icon={meta.icon} /> {compact ? meta.label : `Intelligent check: ${meta.label}`}
    </span>
  )
}

const AdvisoryNote = () => (
  <p className='ic-advisory'>
    <Icon icon='mdi:information-outline' /> Advisory only — it does not approve or
    reject anything. Always check the documents yourself.
  </p>
)

/**
 * Checks and readout for one image (shown next to that image). `image` is
 * idDocument / selfieWithId / proofOfAddress for KYC, idImage for shipper IDs,
 * senderId for a booking's third-party sender ID.
 */
export const IntelligentCheckList = ({
  review,
  image,
}: {
  review?: IntelligentCheck | null
  image: string
}) => {
  if (!review) return null

  if (review.state !== 'done') {
    return (
      <div className='ic-review-card'>
        <div className='ic-head'>
          <h4>
            <Icon icon='mdi:shield-search' /> Intelligent check
          </h4>
          <IntelligentCheckBadge review={review} />
        </div>
        {review.state === 'failed' ? (
          <p className='ic-status'>
            The check could not be completed
            {review.lastError ? `: ${review.lastError}` : '.'}
          </p>
        ) : (
          <p className='ic-status'>Not finished yet — reload in a minute or two.</p>
        )}
      </div>
    )
  }

  const checks = (review.checks || []).filter((c) => c.image === image)
  const worst = worstResult(checks)
  const readout = review.extracted?.[image]

  return (
    <div className='ic-review-card'>
      <div className='ic-head'>
        <h4>
          <Icon icon='mdi:shield-search' /> Intelligent check
        </h4>
        {checks.length > 0 && (
          <span className={`ic-badge ${IMAGE_RESULT_META[worst].className}`}>
            <Icon icon={RESULT_ICON[worst]} />
            {IMAGE_RESULT_META[worst].label}
          </span>
        )}
      </div>

      {checks.length === 0 ? (
        <p className='ic-status'>No checks ran for this image.</p>
      ) : (
        <ul className='ic-checks'>
          {checks.map((c, i) => (
            <li key={`${c.code}-${i}`} className={`ic-check ${c.result}`}>
              <Icon icon={RESULT_ICON[c.result]} />
              <div>
                <p className='label'>{CHECK_LABELS[c.code] || humanise(c.code)}</p>
                {c.detail && <p className='detail'>{c.detail}</p>}
              </div>
            </li>
          ))}
        </ul>
      )}

      {readout && (
        <div className='ic-readout'>
          <p className='readout-title'>What the model read</p>
          <div className='readout-grid'>
            <div>
              <p>Looks like</p>
              <h5>
                {readout.documentType
                  ? READ_TYPE_LABELS[readout.documentType] || humanise(readout.documentType)
                  : '—'}
              </h5>
            </div>
            <div>
              <p>Name</p>
              <h5>{readout.fullName || '—'}</h5>
            </div>
            <div>
              <p>ID number</p>
              <h5 className='mono'>
                {readout.idNumberLast4 ? `****${readout.idNumberLast4}` : '—'}
              </h5>
            </div>
            <div>
              <p>Document date</p>
              <h5>{readout.documentDate || '—'}</h5>
            </div>
            <div>
              <p>Expiry</p>
              <h5>{readout.expiryDate || '—'}</h5>
            </div>
            <div>
              <p>Image issues</p>
              <h5>
                {readout.qualityIssues?.length
                  ? readout.qualityIssues.map(humanise).join(', ')
                  : 'None'}
              </h5>
            </div>
          </div>
        </div>
      )}
      <AdvisoryNote />
    </div>
  )
}

/**
 * Whole-submission summary for the side panel: overall verdict, worst result
 * per image (click to jump to it), model and time.
 */
export const IntelligentCheckSummary = ({
  review,
  images,
  onSelect,
}: {
  review?: IntelligentCheck | null
  images: { key: string; label: string }[]
  onSelect?: (key: string) => void
}) => {
  return (
    <div className='ic-summary-card'>
      <div className='ic-head'>
        <h3>Automated pre-check</h3>
        <IntelligentCheckBadge review={review} />
      </div>
      {!review ? (
        <p className='ic-status'>
          This submission was not checked automatically (it was made before the
          check was switched on, or while it was off).
        </p>
      ) : review.state !== 'done' ? (
        <p className='ic-status'>
          {review.state === 'failed'
            ? `The check could not be completed${review.lastError ? `: ${review.lastError}` : '.'}`
            : 'Queued — results appear here once the check finishes.'}
        </p>
      ) : (
        <>
          <ul className='ic-image-rows'>
            {images.map((img) => {
              const checks = (review.checks || []).filter((c) => c.image === img.key)
              const worst = checks.length ? worstResult(checks) : null
              const problems = checks.filter((c) => c.result !== 'pass').length
              return (
                <li key={img.key}>
                  <button onClick={() => onSelect?.(img.key)}>
                    <span>{img.label}</span>
                    {worst ? (
                      <span className={`ic-check-inline ${worst}`}>
                        <Icon icon={RESULT_ICON[worst]} />
                        {problems ? `${problems} issue${problems === 1 ? '' : 's'}` : 'OK'}
                      </span>
                    ) : (
                      <span className='ic-check-inline'>—</span>
                    )}
                  </button>
                </li>
              )
            })}
          </ul>
          <p className='ic-meta'>
            {review.model ? `${review.model} · ` : ''}
            {review.completedAt ? new Date(review.completedAt).toLocaleString() : ''}
          </p>
        </>
      )}
      <AdvisoryNote />
    </div>
  )
}

export interface PrecheckState {
  status: 'checking' | 'done'
  verdict?: IntelligentCheckVerdict
  issues?: { code: string; severity: 'error' | 'warning'; message: string }[]
}

/** Instant pre-check result under a file field on the agent KYC form. */
export const PrecheckResult = ({ state }: { state?: PrecheckState | null }) => {
  if (!state) return null
  if (state.status === 'checking') {
    return (
      <p className='precheck-result checking'>
        <Icon icon='mdi:progress-clock' /> Checking document…
      </p>
    )
  }
  if (!state.issues?.length) {
    return (
      <p className='precheck-result ok'>
        <Icon icon='mdi:check-circle' /> Looks good
      </p>
    )
  }
  return (
    <ul className='precheck-issues'>
      {state.issues.map((issue, i) => (
        <li key={`${issue.code}-${i}`} className={issue.severity}>
          <Icon icon={issue.severity === 'error' ? 'mdi:close-circle' : 'mdi:alert'} />
          <span>{issue.message}</span>
        </li>
      ))}
    </ul>
  )
}
